import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import useScrollRestoration from "../hooks/useScrollRestoration";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, MapPin } from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, parseISO } from "date-fns";

// Stable colour palette for bands
const BAND_COLOURS = [
  { bg: "bg-primary/15", text: "text-primary", dot: "bg-primary", ring: "ring-primary/30" },
  { bg: "bg-blue-500/15", text: "text-blue-400", dot: "bg-blue-500", ring: "ring-blue-500/30" },
  { bg: "bg-green-500/15", text: "text-green-400", dot: "bg-green-500", ring: "ring-green-500/30" },
  { bg: "bg-purple-500/15", text: "text-purple-400", dot: "bg-purple-500", ring: "ring-purple-500/30" },
  { bg: "bg-pink-500/15", text: "text-pink-400", dot: "bg-pink-500", ring: "ring-pink-500/30" },
  { bg: "bg-teal-500/15", text: "text-teal-400", dot: "bg-teal-500", ring: "ring-teal-500/30" },
];

const AllBandsCalendar = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter-86: preserve scroll position on event detail navigation
  useScrollRestoration({ ready: !loading });
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/calendar/all-bands");
        setEvents(res.data.events || []);
        setBands(res.data.bands || []);
      } catch (e) {
        // Ignore - list stays empty
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const bandColour = useMemo(() => {
    const map = {};
    bands.forEach((b, i) => {
      map[b.band_id] = BAND_COLOURS[i % BAND_COLOURS.length];
    });
    return map;
  }, [bands]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const out = [];
    let d = start;
    while (d <= end) {
      out.push(d);
      d = addDays(d, 1);
    }
    return out;
  }, [cursor]);

  const eventsOnDay = (day) =>
    events.filter((e) => {
      try {
        return isSameDay(parseISO(e.start_time), day);
      } catch {
        return false;
      }
    });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="all-bands-calendar-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight">All Bands Calendar</h1>
          <p className="text-muted-foreground mt-2">Every rehearsal and concert across every band you belong to, in one view.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate("/events")}
          className="hidden md:inline-flex"
          data-testid="switch-to-list-view-btn"
        >
          Back to current band events
        </Button>
      </div>

      {/* Band legend */}
      {bands.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {bands.map((b) => {
            const c = bandColour[b.band_id];
            return (
              <Badge
                key={b.band_id}
                variant="outline"
                className={`${c.bg} ${c.text} border-transparent`}
                data-testid={`band-legend-${b.band_id}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${c.dot}`} />
                {b.band_name}
              </Badge>
            );
          })}
        </div>
      )}

      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              {format(cursor, "MMMM yyyy")}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setCursor(subMonths(cursor, 1))} data-testid="cal-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">
                Today
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="text-center py-20 text-muted-foreground">Loading your bands...</div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1 text-xs text-muted-foreground uppercase tracking-wider px-2 pb-2">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <div key={d} className="text-center py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                  const dayEvents = eventsOnDay(day);
                  const muted = !isSameMonth(day, cursor);
                  const isToday = isSameDay(day, new Date());
                  return (
                    <div
                      key={day.toISOString()}
                      className={`
                        min-h-[100px] rounded-lg p-2 border transition
                        ${muted ? "bg-black/20 border-white/[0.03] opacity-50" : "bg-secondary/20 border-white/[0.05] hover:border-white/10"}
                      `}
                      data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
                    >
                      <div className={`text-xs font-medium mb-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                        {format(day, "d")}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((e) => {
                          const c = bandColour[e.band_id] || BAND_COLOURS[0];
                          const cancelled = (e.status || "").toLowerCase() === "cancelled";
                          return (
                            <button
                              key={e.id}
                              onClick={() => setSelected(e)}
                              className={`w-full text-left text-[11px] px-1.5 py-1 rounded ${c.bg} ${c.text} truncate hover:ring-1 ${c.ring} ${cancelled ? "line-through opacity-70" : ""}`}
                              data-testid={`cal-event-${e.id}`}
                              title={`${e.title} - ${e.band_name}`}
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${c.dot}`} />
                              {format(parseISO(e.start_time), "HH:mm")} {e.title}
                            </button>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-[10px] text-muted-foreground text-center">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Event detail popover */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="event-detail-overlay"
        >
          <Card className="glass-card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="border-b border-white/5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="font-heading text-xl">{selected.title}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">{selected.band_name}</p>
                </div>
                <Badge variant="outline" className="capitalize">{selected.event_type}</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-primary" />
                <span>{format(parseISO(selected.start_time), "EEEE, MMM d, yyyy 'at' HH:mm")}</span>
              </div>
              {selected.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>{selected.location}</span>
                </div>
              )}
              {selected.description && (
                <p className="text-muted-foreground pt-2 line-clamp-3">
                  {(() => {
                    const tmp = document.createElement('div');
                    tmp.innerHTML = selected.description;
                    const text = (tmp.textContent || tmp.innerText || '').trim();
                    return text.length > 300 ? text.slice(0, 300) + '…' : text;
                  })()}
                </p>
              )}
              {(selected.status || "").toLowerCase() === "cancelled" && (
                <Badge variant="outline" className="bg-red-500/15 text-red-400 border-transparent">Cancelled</Badge>
              )}
              <div className="pt-4 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setSelected(null)} data-testid="close-event-detail">
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AllBandsCalendar;
